import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import { useApi, type ApiError, type MetricDefinition, type Insight, type Briefing, type Source } from "../lib/api";
import { useTenant } from "../lib/tenant";

type AgentRun = {
  run_id: string;
  agent?: string;
  kind?: string;
  status: string;
  started_at?: string;
  outputs?: { summary?: string };
};

type Observation = { metric_id: string; ts: string; value: number; confidence?: number };

const TOP_METRICS = [
  { name: "annual_recurring_revenue", label: "ARR", unit: "EUR", positive: true },
  { name: "monthly_recurring_revenue", label: "MRR", unit: "EUR", positive: true },
  { name: "gross_margin_pct", label: "Gross Margin", unit: "%", positive: true },
  { name: "burn_rate", label: "Burn Rate", unit: "EUR", positive: false },
];

const SEVERITY_CLASS: Record<string, string> = {
  info: "sev-info",
  warn: "sev-warn",
  critical: "sev-critical",
};

const TYPE_BADGE: Record<string, string> = {
  anomaly: "🚨 Anomalie",
  trend_break: "📈 Trendbruch",
  threshold: "⚠️ Schwellwert",
  rca: "🔍 Ursachen-Analyse",
};

function formatNumber(n: number, unit: string): string {
  if (!Number.isFinite(n)) return "—";
  if (unit === "EUR") {
    if (Math.abs(n) >= 1_000_000) return `€ ${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1_000) return `€ ${(n / 1_000).toFixed(0)}k`;
    return `€ ${n.toFixed(0)}`;
  }
  if (unit === "%") {
    // Defensive: if backend returns raw count, scale down
    const v = Math.abs(n) > 100 ? n / 10000 : n;
    return `${v.toFixed(1)} %`;
  }
  if (unit === "score" || unit === "ratio") return n.toFixed(2);
  if (unit === "h") return `${n.toFixed(1)} h`;
  if (unit === "months") return `${n.toFixed(1)} Mo`;
  return n.toFixed(0);
}

function KpiCard({ metric }: { metric: { name: string; label: string; unit: string; positive: boolean } }) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const isDemo = effectiveTenantId === "tnt_demo";
  const metrics = useQuery({
    queryKey: ["metrics", effectiveTenantId],
    queryFn: () => api.get<MetricDefinition[]>("/metric-definitions"),
    staleTime: 5 * 60_000,
  });
  const m = (metrics.data ?? []).find(x => x.name === metric.name);
  const obs = useQuery({
    queryKey: ["obs", effectiveTenantId, m?.id],
    queryFn: () => api.get<Observation[]>(`/kpi-observations?metric_id=${m!.id}`),
    enabled: !!m,
    staleTime: 60_000,
  });

  const data = obs.data ?? [];
  const current = data.length > 0 ? data[data.length - 1].value : null;
  const previous = data.length > 1 ? data[data.length - 2].value : null;
  const delta = current != null && previous != null ? ((current - previous) / Math.abs(previous)) * 100 : null;
  // direction from metric definition wins over hardcoded `positive`
  const direction = m?.direction ?? (metric.positive ? "up_is_favorable" : "down_is_favorable");
  const deltaGood = delta != null
    ? (direction === "up_is_favorable" ? delta >= 0 :
       direction === "down_is_favorable" ? delta <= 0 :
       null)
    : null;

  return (
    <Link to="/app/metrics" className="kpi-card-pro">
      <div className="kpi-pro-hdr">
        <span className="kpi-pro-label">{metric.label}</span>
        {delta != null && (
          <span className={`kpi-pro-delta ${deltaGood ? "good" : "bad"}`}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      <div className="kpi-pro-value">{current != null ? formatNumber(current, metric.unit) : "—"}</div>
      <div className="kpi-pro-sparkline">
        {data.length > 0 && (
          <ResponsiveContainer width="100%" height={48}>
            <AreaChart data={data.map(d => ({ v: d.value, ts: d.ts }))}>
              <defs>
                <linearGradient id={`grad-${metric.name}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={deltaGood === false ? "#e75f6b" : "#35c88a"} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={deltaGood === false ? "#e75f6b" : "#35c88a"} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={deltaGood === false ? "#e75f6b" : "#35c88a"}
                    strokeWidth={2} fill={`url(#grad-${metric.name})`} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="kpi-pro-foot muted small">{data.length} Datenpunkte{isDemo && <span className="data-quality-badge demo" title="Sandbox-Daten">Demo</span>}</div>
    </Link>
  );
}

function InsightCard({ insight, metricName }: { insight: Insight; metricName?: string }) {
  return (
    <div className={`insight-card ${SEVERITY_CLASS[insight.severity] ?? ""}`}>
      <div className="insight-hdr">
        <span className="insight-type-badge">{TYPE_BADGE[insight.type] ?? insight.type}</span>
        <span className="insight-metric muted small">{metricName ?? insight.metric_id}</span>
      </div>
      <h3 className="insight-what">{insight.what_happened}</h3>
      {insight.why_hypothesis && (
        <div className="insight-why">
          <span className="muted small">WARUM</span>
          <p>{insight.why_hypothesis}</p>
        </div>
      )}
      <div className="insight-actions">
        <div className="muted small">WAS TUN</div>
        {insight.what_to_do.map((a, i) => (
          <div key={i} className="insight-action">
            <span className="action-tmpl">{a.action_template}</span>
            {a.expected_impact && <span className="action-impact">{a.expected_impact}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function renderMarkdownLight(text: string): JSX.Element {
  // Minimal markdown: **bold**, *italic*, line breaks, bullet points
  const lines = text.split("\n");
  return (
    <div className="briefing-md">
      {lines.map((line, i) => {
        if (line.startsWith("- ") || line.startsWith("* ")) {
          return <li key={i}>{renderInline(line.slice(2))}</li>;
        }
        if (!line.trim()) return <br key={i} />;
        return <p key={i}>{renderInline(line)}</p>;
      })}
    </div>
  );
}

function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<strong key={i++}>{m[1]}</strong>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

type BriefingExt = Briefing & { _title?: string; _summary?: string };

type DataTrustBlockDetail = {
  code?: string;
  surface?: string;
  tenant_id?: string;
  trust_gate_status?: string;
  quality_threshold?: number;
  next_actions?: string[];
};

function extractDataTrustBlock(error: unknown): DataTrustBlockDetail | null {
  const apiError = error as ApiError | undefined;
  const details = apiError?.details;
  const candidate = details && typeof details === "object" && "detail" in details
    ? (details as { detail?: unknown }).detail
    : details;
  if (!candidate || typeof candidate !== "object") return null;
  const detail = candidate as DataTrustBlockDetail;
  return detail.code === "data_trust_gate_blocked" ? detail : null;
}

function BriefingTrustGate({ detail }: { detail: DataTrustBlockDetail }) {
  return (
    <section className="cockpit-section">
      <div className="section-hdr">
        <h2>Briefings blockiert</h2>
        <span className="pill bad">Data Trust Gate</span>
      </div>
      <div className="lineage-card briefing-trust-block">
        <p>
          Executive Briefings für Tenant <strong>{detail.tenant_id ?? "unbekannt"}</strong> bleiben verborgen,
          bis Quellen und Quality Report vertrauenswürdig sind.
        </p>
        <p className="muted small">
          Status {detail.trust_gate_status ?? "blocked"}
          {typeof detail.quality_threshold === "number" && <> · Schwelle {detail.quality_threshold}/100</>}
        </p>
        {(detail.next_actions ?? []).length > 0 && (
          <ul className="lineage-list">
            {(detail.next_actions ?? []).slice(0, 3).map((action) => (
              <li key={action}>
                <span className="pill warn">next</span>
                <span>{action}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="brief-cta">
          <Link className="btn ghost" to="/app/sources">Quellen prüfen</Link>
          <Link className="btn ghost" to="/app/command-center">Command Center</Link>
        </div>
      </div>
    </section>
  );
}

function SandboxBanner() {
  return (
    <div className="sandbox-banner">
      <strong>📦 Demo-Modus</strong> — Sie sind im Sandbox-Tenant <code>tnt_demo</code> mit{" "}
      <strong>Beispieldaten</strong> (frei erfunden, nicht echt). Um echte Zahlen aus Ihren
      eigenen Daten zu sehen, starten Sie das <Link to="/app/onboarding">Onboarding</Link> und
      laden Daten unter <Link to="/app/sources">Quellen</Link> hoch.
    </div>
  );
}

function EmptyStateFunnel() {
  return (
    <section className="empty-state-funnel">
      <h2>Willkommen — los gehts in 5 Schritten</h2>
      <p className="subtitle">
        Damit Sie verlässliche KPIs sehen, brauchen wir zuerst Ihre Daten. Wir zeigen Ihnen niemals
        erfundene Zahlen — Datenqualität ist das A und O.
      </p>
      <ol className="funnel-steps">
        <li className="funnel-step done">
          <div className="step-num">1</div>
          <div className="step-body">
            <strong>Registrieren</strong>
            <span className="muted small">✓ erledigt — Sie sind angemeldet</span>
          </div>
        </li>
        <li className="funnel-step active">
          <div className="step-num">2</div>
          <div className="step-body">
            <strong>Firma anlegen</strong>
            <span className="muted small">Firmenname, Branche, Größe, Ziele</span>
            <Link className="btn primary small" to="/app/onboarding">Jetzt anlegen</Link>
          </div>
        </li>
        <li className="funnel-step">
          <div className="step-num">3</div>
          <div className="step-body">
            <strong>KI-Klärungs-Dialog</strong>
            <span className="muted small">5-Min-Gespräch über Ihr Geschäftsmodell — damit wir die richtigen KPIs vorschlagen können</span>
          </div>
        </li>
        <li className="funnel-step">
          <div className="step-num">4</div>
          <div className="step-body">
            <strong>Daten verbinden</strong>
            <span className="muted small">CSV-Upload, Stripe-Sandbox, Crawl4AI oder Nimble — mit 5-Dimensionen-Datenqualitäts-Check</span>
          </div>
        </li>
        <li className="funnel-step">
          <div className="step-num">5</div>
          <div className="step-body">
            <strong>KPI-Discovery + Approval</strong>
            <span className="muted small">Der Agent schlägt 10-15 Kennzahlen aus IHREN Daten vor. Sie wählen aus.</span>
          </div>
        </li>
      </ol>
      <div className="funnel-cta">
        <p className="muted">
          Während Sie das einrichten, können Sie unseren KI-Berater bereits jetzt zu allgemeinen KPI-,
          Branchen- und Strategie-Fragen befragen.
        </p>
        <Link className="btn ghost" to="/app/sources">Daten verbinden →</Link>
      </div>
    </section>
  );
}

export default function CockpitPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const isSandbox = effectiveTenantId === "tnt_demo";

  const sources = useQuery({
    queryKey: ["sources", effectiveTenantId],
    queryFn: () => api.get<Source[]>("/sources"),
  });
  const hasOwnData = (sources.data ?? []).length > 0;

  const metrics = useQuery({
    queryKey: ["metrics", effectiveTenantId],
    queryFn: () => api.get<MetricDefinition[]>("/metric-definitions"),
    enabled: isSandbox || hasOwnData,
  });
  const insights = useQuery({
    queryKey: ["insights", effectiveTenantId],
    queryFn: () => api.get<Insight[]>("/insights"),
    refetchInterval: 60_000,
  });
  const briefings = useQuery({
    queryKey: ["briefings", effectiveTenantId],
    queryFn: () => api.get<BriefingExt[]>("/briefings"),
    refetchInterval: 60_000,
  });
  const runs = useQuery({
    queryKey: ["agent-runs", effectiveTenantId],
    queryFn: () => api.get<AgentRun[]>("/agent-runs"),
    refetchInterval: 30_000,
  });
  const briefingsBlock = extractDataTrustBlock(briefings.error);

  const metricById = useMemo(() => {
    const m: Record<string, MetricDefinition> = {};
    (metrics.data ?? []).forEach(x => { m[x.id] = x; });
    return m;
  }, [metrics.data]);

  const topInsights = (insights.data ?? []).slice(0, 3);

  // newest delivered or ready briefing
  const latestBriefing = (briefings.data ?? [])
    .filter(b => b.status === "delivered" || b.status === "ready")
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];

  const pending = (runs.data ?? []).filter(r => r.status === "queued" || r.status === "blocked");

  // Empty-State: no own sources AND not sandbox -> show onboarding funnel only
  if (!isSandbox && !hasOwnData && !sources.isLoading) {
    return (
      <div className="page cockpit-pro">
        <div className="cockpit-hero">
          <h1>Cockpit</h1>
        </div>
        <EmptyStateFunnel />
      </div>
    );
  }

  return (
    <div className="page cockpit-pro">
      {isSandbox && <SandboxBanner />}
      <div className="cockpit-hero">
        <h1>Klassisches Cockpit{isSandbox && <span className="hero-suffix muted small"> · Demo-Modus</span>}</h1>
        <p className="subtitle">
          {isSandbox
            ? "Beispiel-Cockpit mit Sandbox-Daten — was bei echten Daten ähnlich aussehen würde."
            : "Die frühere Startansicht. Die aktuelle Steuerung mit Ampeln, Gate-Matrix und KI-Briefing finden Sie im Steuerkreis-Cockpit."}
        </p>
      </div>

      {/* KPI Cards mit Sparkline */}
      <section className="kpi-pro-grid">
        {TOP_METRICS.map(m => <KpiCard key={m.name} metric={m} />)}
      </section>

      {/* Insights — der eigentliche Wert */}
      <section className="cockpit-section">
        <div className="section-hdr">
          <h2>Was Ihre KPIs gerade sagen</h2>
          <Link to="/app/metrics" className="muted small">alle Metriken →</Link>
        </div>
        {insights.isLoading && <p className="muted">Lade Insights…</p>}
        {!insights.isLoading && topInsights.length === 0 && (
          <p className="muted">Aktuell keine Insights. Verbinden Sie Daten in <Link to="/app/sources">Quellen</Link>, der Discovery-Agent erzeugt automatisch Erkenntnisse.</p>
        )}
        <div className="insights-grid">
          {topInsights.map(i => (
            <InsightCard
              key={i.id}
              insight={i}
              metricName={metricById[i.metric_id]?.display_name ?? metricById[i.metric_id]?.name}
            />
          ))}
        </div>
      </section>

      {/* Letztes Briefing */}
      {briefingsBlock && <BriefingTrustGate detail={briefingsBlock} />}
      {latestBriefing && (
        <section className="cockpit-section">
          <div className="section-hdr">
            <h2>Letztes Briefing — {latestBriefing._title || latestBriefing.type}</h2>
            <Link to="/app/briefings" className="muted small">alle Briefings →</Link>
          </div>
          <div className="briefing-preview">
            <div className="briefing-meta muted small">
              {latestBriefing.audience} · {latestBriefing.type} ·{" "}
              {latestBriefing.created_at && new Date(latestBriefing.created_at).toLocaleString("de-DE")}
            </div>
            {latestBriefing._summary && renderMarkdownLight(latestBriefing._summary)}
          </div>
        </section>
      )}

      {/* Pending Approvals + Recent Runs */}
      <section className="cockpit-section">
        <div className="section-hdr">
          <h2>Agent-Aktivität</h2>
          <Link to="/app/agents" className="muted small">alle Runs →</Link>
        </div>
        {pending.length > 0 && (
          <div className="pending-banner">
            <strong>{pending.length}</strong> Run{pending.length === 1 ? "" : "s"} warten auf Ihre Entscheidung.{" "}
            <Link to="/app/agents" className="btn primary small">Jetzt reviewen</Link>
          </div>
        )}
        <ul className="runs-mini">
          {(runs.data ?? []).slice(0, 5).map(r => (
            <li key={r.run_id} className={`run-mini ${r.status}`}>
              <span className="run-status-dot" />
              <span className="run-agent">{r.agent ?? r.kind ?? "agent"}</span>
              <span className="run-summary muted small">
                {r.outputs?.summary?.slice(0, 80) ?? `status: ${r.status}`}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Driver Tree Vorschau */}
      <section className="cockpit-section">
        <div className="section-hdr">
          <h2>Driver Tree</h2>
          <Link to="/app/trees" className="muted small">Editor öffnen →</Link>
        </div>
        <p className="muted">
          Wie hängen Ihre KPIs zusammen? Der Driver Tree zeigt Korrelationen mit Konfidenz-Werten —
          z.B. „LTV → Churn -95% Korrelation".
        </p>
      </section>
    </div>
  );
}
